import { Controller } from "@hotwired/stimulus"

// Drives communities/_sticky_purchase_bar.
//
// The bar slides up once the page is scrolled past `offset` AND none of the
// watched full purchase forms are on screen — while a real form is visible the
// user already has the better version of it in front of them. With `fold` set,
// the bar also shows while that button (the hero's own CTA) is still below the
// first screen: on a phone the hero form can be half visible with its button
// out of reach, and the buyer should not have to scroll to buy.
export default class extends Controller {
  static targets = ["panel", "amount", "label"]
  static values = {
    watch: { type: String, default: "#gift_card_form_hero" },
    anchor: { type: String, default: "" },
    offset: { type: Number, default: 320 },
    fold: { type: String, default: "" },
    product: String,
    sharedAmount: Boolean,
    cta: String
  }

  connect() {
    // A restored Turbolinks snapshot may still contain the visible bar.
    // Always reconcile the DOM on the first refresh after connecting.
    this.visible = null
    this.formsOnScreen = new Set()
    this.frame = null

    const watched = this.watchValue ? Array.from(document.querySelectorAll(this.watchValue)) : []
    if (watched.length && "IntersectionObserver" in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.formsOnScreen.add(entry.target)
          } else {
            this.formsOnScreen.delete(entry.target)
          }
        })
        this.refresh()
      }, { rootMargin: "-72px 0px -72px 0px" }) // ignore slivers peeking past the edges
      watched.forEach((el) => this.observer.observe(el))
    }

    this.onScroll = () => {
      if (this.frame) return
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null
        this.refresh()
      })
    }
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll, { passive: true })

    this.trackAmountChanges()
    this.refresh()
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.frame) window.cancelAnimationFrame(this.frame)
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onScroll)
    if (this.mirrorAmount) document.removeEventListener("change", this.mirrorAmount)
    if (this.syncAmount) document.removeEventListener("atento:amount-changed", this.syncAmount)
    document.body.classList.remove("sticky-purchase-open")
  }

  refresh() {
    this.toggle(this.foldCtaBelowScreen() || (window.scrollY > this.offsetValue && this.formsOnScreen.size === 0))
  }

  foldCtaBelowScreen() {
    if (!this.foldValue) return false
    const cta = document.querySelector(this.foldValue)
    if (!cta) return false
    return cta.getBoundingClientRect().bottom > window.innerHeight - 8
  }

  toggle(show) {
    if (show === this.visible) return
    this.visible = show

    this.element.classList.toggle("translate-y-full", !show)
    this.element.classList.toggle("opacity-0", !show)
    this.element.setAttribute("aria-hidden", show ? "false" : "true")
    this.element.inert = !show
    // Lets the stylesheet lift the HubSpot chat launcher, which otherwise sits on the bar's button.
    document.body.classList.toggle("sticky-purchase-open", show)

    if (this.hasPanelTarget) {
      this.panelTarget.classList.toggle("pointer-events-auto", show)
      this.panelTarget.classList.toggle("pointer-events-none", !show)
    }
  }

  // "Other amount" is not a real amount: bounce back to the full form, which is
  // the only place a free value can be typed.
  amountChanged(event) {
    const select = event.target
    if (select.value !== "__custom") {
      this.lastAmount = select.value
      this.updateLabel(select.value)
      if (this.sharedAmountValue) {
        this.element.dispatchEvent(new CustomEvent("atento:amount-changed", {
          bubbles: true,
          detail: { productId: this.productValue, amount: select.value }
        }))
      }
      return
    }

    select.value = this.lastAmount || select.options[0].value
    const anchor = this.anchorValue && document.querySelector(this.anchorValue)
    if (anchor) {
      anchor.scrollIntoView({ behavior: "smooth", block: "center" })
      if (this.sharedAmountValue) {
        const form = anchor.matches('[data-controller~="gift-amount"]')
          ? anchor : anchor.querySelector('[data-controller~="gift-amount"]')
        if (form) form.dispatchEvent(new CustomEvent("atento:custom-amount"))
      }
    }
  }

  updateLabel(value) {
    this.labelTargets.forEach((target) => { target.textContent = this.ctaValue.replace("__AMOUNT__", value) })
  }

  setAmount(value) {
    const previousCustom = this.amountTarget.querySelector("[data-sticky-custom]")
    if (previousCustom) previousCustom.remove()
    const match = Array.from(this.amountTarget.options).some((option) => option.value === value)
    if (!match) {
      const option = new Option(`${value} €`, value)
      option.dataset.stickyCustom = "true"
      this.amountTarget.add(option, this.amountTarget.options.length - 1)
    }
    this.amountTarget.value = value
    this.lastAmount = value
    this.updateLabel(value)
  }

  // Keep the bar in step with whatever the user picked in the full form, so
  // scrolling away doesn't silently reset their choice.
  trackAmountChanges() {
    if (!this.hasAmountTarget) return
    this.lastAmount = this.amountTarget.value
    this.updateLabel(this.lastAmount)

    if (this.sharedAmountValue) {
      // Native select state can reset when Turbolinks clones a page. Restore
      // the amount from the full form's canonical input before showing the bar.
      const form = this.watchValue && document.querySelector(this.watchValue)
      const input = form && form.querySelector('[data-gift-amount-target="input"]')
      if (input && input.validity.valid) this.setAmount(input.value)

      this.syncAmount = (event) => {
        if (event.detail.productId !== this.productValue || event.target === this.element) return
        this.setAmount(event.detail.amount)
      }
      document.addEventListener("atento:amount-changed", this.syncAmount)
    }

    this.mirrorAmount = (event) => {
      const field = event.target
      if (!field.name || this.element.contains(field)) return
      if (this.watchValue && !field.closest(this.watchValue)) return
      if (!["amount", "amount_custom", "custom_amount"].includes(field.name)) return
      if (field.type === "radio" && !field.checked) return
      if (field.validity && !field.validity.valid) return

      const value = String(field.value || "")
      if (!Number.isFinite(Number(value)) || Number(value) <= 0) return
      this.setAmount(value)
    }
    document.addEventListener("change", this.mirrorAmount)
  }
}
